/**
 * English copy for the public stablecoin checkout (page spec
 * `invoice-pay-checkout`, route `/pay`). One module for the whole page group so
 * no component in `pay/` carries a user-facing string of its own.
 *
 * Several strings here are **the contract's copy**, quoted verbatim from
 * `product-design/specs/09-invoicing/stablecoin-invoicing-spec.md` (PAY-00,
 * PAY-01 and "Draft UI Copy (public checkout)"). They are not paraphrase-able:
 * two of them are the payer's only signal in a fraud case.
 *
 *   - unknown / revoked / malformed token: "This payment link is not active."
 *   - attestation failure: "We can't verify this payment address. Do not pay."
 *   - screening pending: "Payment received — under review."
 *   - anti-phishing, always visible: "Only pay to the address shown on this
 *     page. Anyone contacting you with a different address is attempting
 *     fraud."
 *
 * Two copy rules hold everywhere below, and both are asserted by the unit
 * suite:
 *
 *   - **No string may reveal whether a token exists.** The not-active,
 *     rate-limited and error states are reachable from a bad link and from a
 *     real one that is simply unavailable, and a payer must not be able to
 *     probe for valid links by comparing them (`token_existence_leaked` in the
 *     page spec's bug signals).
 *   - **A refusal is quoted, not explained away.** The payer is told the
 *     address cannot be verified and told not to pay; the copy never offers a
 *     way to proceed anyway.
 */

export const PAY_COPY = {
  /* ---------------------------------------------------------------- frame */

  pageTitle: "Pay invoice",
  pageSubtitle: "Public checkout for a stablecoin invoice.",
  /**
   * Honest note that the invoicing service behind the public pay contract has
   * no backend in this environment. It is the `reason` line the other
   * invoicing pages carry on their `NotAvailableState`, kept here as plain
   * text because this page does make real calls when a token is present.
   * It must stay free of the phrases the route test bans ("coming soon",
   * "not implemented", "check the prd", "todo", "lorem ipsum").
   */
  environmentNote:
    "Reads the public pay contract's invoice, declaration, address, status and receipt routes. Those routes have no backend in this environment, so what you see below is a live refusal, never sample data.",

  /* --------------------------------------------------------------- states */

  loadingLabel: "Loading this payment link...",

  /**
   * One shared state for unknown, revoked, malformed and over-long tokens
   * (spec line 261). It is deliberately the same sentence in all four cases.
   */
  notActiveTitle: "This payment link is not active.",
  notActiveDescription:
    "Check the whole link you were sent, or ask the issuer to send a new one. Nothing has been charged and no payment has been requested from you.",

  /** Expired and void invoices (spec line 285). */
  closedTitle: "This invoice is no longer accepting payments. Please contact the issuer.",

  rateLimitedTitle: "This link is busy right now",
  rateLimitedDescription: "Please wait a moment and open the link again.",
  rateLimitedRetry: "Try again",

  errorTitle: "We couldn't load this payment link",
  errorDescription:
    "Nothing was charged. Try again in a moment — if it keeps failing, give the reference below to the issuer.",
  errorRetry: "Try again",

  /** A 403 from the service: the refusal is the service's decision, quoted. */
  deniedTitle: "This payment link can't be opened",
  deniedDescription:
    "The payment service refused this request. Nothing was charged. Contact the issuer and quote the reference below.",

  offlineTitle: "We couldn't reach the payment service",
  offlineDescription: "Check your connection and try again. Nothing was charged.",

  retry: "Try again",

  /* --------------------------------------------------------------- payee */

  payeeHeading: "Payee",
  payeeLegalNameLabel: "Verified legal name",
  payeeVerifiedNote: "Verified by the issuer's business (KYB) checks.",

  /* ------------------------------------------------------------- invoice */

  invoiceHeading: "Invoice summary",
  invoiceNumberLabel: "Invoice number",
  invoiceAmountLabel: "Amount due",
  invoiceDueDateLabel: "Due date",
  invoiceAssetLabel: "Asset",

  /* --------------------------------------------------------- declaration */

  declarationHeading: "Before you pay",
  declarationIntro:
    "The issuer needs to record who is paying. Fill this in first — it is locked once a deposit address is issued.",
  declarationLegalNameLabel: "Your legal name",
  declarationLegalNamePlaceholder: "As it appears on your records",
  declarationCountryLabel: "Country",
  declarationCountryHint: "Two-letter country code, for example DE or GB.",
  declarationEmailLabel: "Contact email",
  declarationEmailHint: "The issuer uses this for the receipt and for refund confirmations.",
  declarationSubmit: "Save and continue",
  declarationSaving: "Saving...",
  declarationFrozenTitle: "Declaration recorded",
  declarationFrozenNote: "It is locked now that a deposit address has been issued. Contact the issuer to change it.",
  declarationRequiredNote: "Complete this step before an address can be requested.",

  /* ------------------------------------------------------------- network */

  networkHeading: "Choose a network and asset",
  networkIntro: "Only networks the issuer has certified for this invoice are listed.",
  networkLabel: "Network",
  networkRequestAddress: "Get deposit address",
  networkRequesting: "Requesting...",

  /* ------------------------------------------------------------- address */

  addressHeading: "Deposit address",
  addressAntiPhishing:
    "Only pay to the address shown on this page. Anyone contacting you with a different address is attempting fraud.",
  addressNetworkLabel: "Network",
  addressValueLabel: "Address",
  addressChecksumLabel: "Checksum",
  addressChecksumHint: "Compare these characters against your wallet before sending.",
  addressQrLabel: "QR code of the deposit address",
  addressUnpaidNote: "Send only the invoice amount, in the asset shown, on this network.",
  /** The refusal. Two sentences, both the contract's, and nothing after them. */
  addressRefusedTitle: "We can't verify this payment address. Do not pay.",
  addressRefusedBody:
    "The address the service returned could not be checked against the issuer's signing keys, so it is not shown. Do not send funds to any address you were sent separately. Contact the issuer.",
  addressRefusedCodeLabel: "Reason",

  /* -------------------------------------------------------------- status */

  statusHeading: "Payment status",
  statusLabel: "Status",
  statusConfirmationsLabel: "Confirmations",
  statusConfirmationsValue: (have: number, need: number) => `${have} of ${need}`,
  statusScreeningPending: "Payment received — under review.",
  statusUnpaidNote: "Nothing has arrived yet. This page updates by itself once a payment is seen.",
  statusPollingNote: "Checking for updates...",
  statusStaleNote: "Live updates stopped. The last status is shown; reload the page for the current one.",
  statusRefresh: "Check now",
  statusPaidNote: "This invoice is settled.",

  /* ------------------------------------------------------------- receipt */

  receiptHeading: "Receipt",
  receiptIntro: "Available once the payment has reached finality.",
  receiptDownload: "Download receipt",
  receiptPending: "The receipt appears here when the payment is final.",
  receiptReviewNote: "Payment received — under review. The receipt is issued after the review completes.",
  receiptUnavailable: "The service did not return a downloadable receipt for this payment.",

  /* -------------------------------------------------------------- refund */

  refundHeading: "Refund address",
  refundIntro:
    "A refund is being sent back to you. Give the address it should go to — you will confirm it with a one-time code sent to your email.",
  refundAddressLabel: "Refund address",
  refundAddressPlaceholder: "The address your wallet controls",
  refundSubmit: "Request confirmation code",
  refundSubmitting: "Sending...",
  refundCodeLabel: "One-time code",
  refundCodeHint: "Sent to your contact email. It expires quickly.",
  refundConfirm: "Confirm refund address",
  refundConfirming: "Confirming...",
  refundConfirmedTitle: "Refund address confirmed",
  refundConfirmedBody: "The issuer will send the refund to this address.",
  refundPendingNote: "Enter the code from your email to confirm the address. Until then the refund is on hold.",

  /* -------------------------------------------------------------- footer */

  footerSupport: "Reference",
  footerNoThirdParty:
    "This page loads no third-party scripts and sends the payment link token only to the issuer's own payment service.",
} as const;

/** Label for a network the service did not name. Never a code, never blank. */
export const UNNAMED_NETWORK_LABEL = "Unnamed network";
